/**
 * Loud, dashboard-wide alert when a live chat is assigned or transferred to
 * THIS agent. Three coordinated channels, per spec:
 *
 *   1. Socket push     — server emits `livechat:incoming` to `user:{agentId}`
 *   2. Browser notice  — desktop Notification (requireInteraction + sound)
 *   3. In-app banner    — persistent top-center toast with Accept / View Queue
 *
 * The chime repeats every 30s while any incoming chat is still unaccepted and
 * stops the moment the agent accepts, another agent takes it, or it ends.
 */
import { useEffect } from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Bell, Check, ListChecks, X, MapPin, Clock } from 'lucide-react'
import { post } from '@/lib/api'
import { useSocketEvent } from '@/lib/socket'
import { playIncomingChime } from '@/lib/notificationSound'

interface IncomingChat {
  chatId: string
  visitorName: string | null
  visitorCountry: string | null
  lastMessage: string | null
  priority: string
  waitTime: number
}

export function IncomingChatNotifier() {
  const [pending, setPending] = useState<IncomingChat[]>([])
  const navigate = useNavigate()
  const qc = useQueryClient()

  const remove = (chatId: string) =>
    setPending((p) => p.filter((c) => c.chatId !== chatId))

  // Request desktop-notification permission once, on first mount after login.
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => undefined)
    }
  }, [])

  // (1) Socket push → enqueue + (2) browser notification + sound.
  useSocketEvent('livechat:incoming', (e) => {
    setPending((prev) =>
      prev.some((c) => c.chatId === e.chatId)
        ? prev
        : [
            {
              chatId: e.chatId,
              visitorName: e.visitorName,
              visitorCountry: e.visitorCountry,
              lastMessage: e.lastMessage,
              priority: e.priority,
              waitTime: e.waitTime,
            },
            ...prev,
          ],
    )
    playIncomingChime()
    showDesktopNotification(e.visitorName, e.lastMessage, e.chatId)
  })

  // Banner clears when another agent takes the chat or it ends.
  useSocketEvent('livechat:accepted', (e) => remove(e.chatId))
  useSocketEvent('livechat:resolved', (e) => remove(e.chatId))
  useSocketEvent('livechat:closed', (e) => remove(e.chatId))

  // Re-chime every 30s while anything is still waiting to be accepted.
  const hasPending = pending.length > 0
  useEffect(() => {
    if (!hasPending) return
    const id = window.setInterval(() => playIncomingChime(), 30_000)
    return () => window.clearInterval(id)
  }, [hasPending])

  const accept = useMutation({
    mutationFn: (chatId: string) => post(`/api/v1/live-chat/${chatId}/accept`, {}),
    onSuccess: (_data, chatId) => {
      remove(chatId)
      qc.invalidateQueries({ queryKey: ['live-chat', 'queue'] })
      qc.invalidateQueries({ queryKey: ['live-chat', 'stats'] })
      qc.invalidateQueries({ queryKey: ['tenant', 'usage'] })
      navigate(`/live-chat/inbox?id=${chatId}`)
    },
    onError: (err: Error, chatId) => {
      // Most likely already accepted elsewhere — drop the banner either way.
      toast.error(err.message)
      remove(chatId)
    },
  })

  if (!hasPending) return null

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] flex w-[min(92vw,420px)] flex-col gap-2">
      {pending.map((c) => (
        <IncomingBanner
          key={c.chatId}
          chat={c}
          accepting={accept.isPending && accept.variables === c.chatId}
          onAccept={() => accept.mutate(c.chatId)}
          onViewQueue={() => navigate('/live-chat/inbox')}
          onDismiss={() => remove(c.chatId)}
        />
      ))}
    </div>
  )
}

function IncomingBanner({
  chat, accepting, onAccept, onViewQueue, onDismiss,
}: {
  chat: IncomingChat
  accepting: boolean
  onAccept: () => void
  onViewQueue: () => void
  onDismiss: () => void
}) {
  const name = chat.visitorName?.trim() || 'Website visitor'
  const urgent = chat.priority === 'urgent' || chat.priority === 'high'
  return (
    <div
      className="overflow-hidden rounded-xl border bg-white"
      style={{
        borderColor: urgent ? '#FCA5A5' : '#E4DCFF',
        boxShadow: '0 12px 32px rgba(91,79,207,0.20)',
        animation: 'vela-incoming-in 220ms ease-out',
      }}
      role="alert"
    >
      {/* Accent strip */}
      <div style={{ height: 4, background: urgent ? '#EF4444' : '#10B981' }} />
      <div className="p-3.5">
        <div className="flex items-start gap-3">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
            style={{ background: urgent ? '#FEE2E2' : '#ECFDF5' }}
          >
            <Bell className="h-[18px] w-[18px]" style={{ color: urgent ? '#991B1B' : '#065F46' }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-bold text-[#1F1D35] truncate">
                New chat: {name}
              </span>
              {urgent ? (
                <span className="rounded-full bg-[#FEE2E2] px-1.5 py-[1px] text-[10px] font-semibold uppercase text-[#991B1B]">
                  {chat.priority}
                </span>
              ) : null}
              <button
                onClick={onDismiss}
                className="ml-auto -mr-1 -mt-1 rounded-md p-1 text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#4B5563]"
                aria-label="Dismiss"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            {chat.lastMessage ? (
              <div className="mt-0.5 text-xs italic text-[#4B5563] line-clamp-2">
                “{chat.lastMessage}”
              </div>
            ) : (
              <div className="mt-0.5 text-xs text-[#9CA3AF]">A visitor needs help.</div>
            )}
            <div className="mt-1 flex items-center gap-3 text-[11px] text-[#9CA3AF]">
              {chat.visitorCountry ? (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {chat.visitorCountry}
                </span>
              ) : null}
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {chat.waitTime <= 0 ? 'just now' : `${chat.waitTime}m waiting`}
              </span>
            </div>
            <div className="mt-2.5 flex items-center gap-2">
              <button
                onClick={onAccept}
                disabled={accepting}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#10B981] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#0EA572] disabled:opacity-50"
              >
                <Check className="h-3.5 w-3.5" /> {accepting ? 'Accepting…' : 'Accept'}
              </button>
              <button
                onClick={onViewQueue}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#E4DCFF] bg-[#F0ECFF] px-3 py-1.5 text-xs font-semibold text-[#5B4FCF] transition-colors hover:bg-[#E4DCFF]"
              >
                <ListChecks className="h-3.5 w-3.5" /> View Queue
              </button>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes vela-incoming-in{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  )
}

function showDesktopNotification(
  name: string | null,
  lastMessage: string | null,
  chatId: string,
): void {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
    const n = new Notification('New chat transfer', {
      body: `${name?.trim() || 'A visitor'} needs help${lastMessage ? `: "${lastMessage}"` : ''}`,
      icon: '/favicon.ico',
      tag: chatId, // dedupe repeat fires for the same chat
      requireInteraction: true, // stays until the agent dismisses/clicks
    })
    n.onclick = () => {
      window.focus()
      n.close()
    }
  } catch {
    /* notifications unavailable — banner + sound still fire */
  }
}
